import React, {
    FC,
    ReactNode,
    createContext,
    useContext,
    useState,
} from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useGet } from '../hooks/useApi';
import { userApi } from '../APIs/userApi';
import { mutate } from 'swr';

export interface User {
    tenantId: string;
    userId: string;
    email: string;
    /** @deprecated use firstName */
    givenName: string;
    firstName: string;
    /** @deprecated use lastName */
    familyName: string;
    lastName: string;
    role: string;
    phoneNumber: string;
}

type AuthContextType = {
    isLoading: boolean;
    isAuthenticated: boolean;
    user?: User | null;
    isAdmin: () => boolean;
    isUser: () => boolean;
    isSuperAdmin: () => boolean;
    isExternalUser: () => boolean;
    signOut: () => void;
};

const AuthContext = createContext<AuthContextType>({
    isLoading: false,
    isAuthenticated: false,
    user: null,
    isAdmin: () => false,
    isUser: () => false,
    isSuperAdmin: () => false,
    isExternalUser: () => false,
    signOut: () => {},
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { authStatus, user, signOut: cognitoLogout } = useAuthenticator();
    const [wasAuthenticated, setWasAuthenticated] = useState(false);
    const attributes = useGet(
        user?.userId ? userApi.get.userAttributes(user.userId) : null
    );

    const role: string = attributes.data?.role;

    const isAdmin = () => role === 'Admin';
    const isUser = () => role === 'User';
    const isSuperAdmin = () => role === 'SuperAdmin';
    const isExternalUser = () => role === 'External';
    if (authStatus === 'authenticated' && !wasAuthenticated) {
        setWasAuthenticated(true);
    }
    if (authStatus === 'unauthenticated' && wasAuthenticated) {
        document.location.reload();
    }
    const signOut = () => {
        setTimeout(() => {
            cognitoLogout();
            mutate((key) => true, undefined, { revalidate: false });
        }, 0);
    };

    if (attributes.error) {
        throw attributes.error;
    }

    return (
        <AuthContext.Provider
            value={{
                isLoading: attributes.isLoading || authStatus === 'configuring',
                isAuthenticated: authStatus === 'authenticated',
                user: attributes.data,
                isAdmin,
                isUser,
                isSuperAdmin,
                isExternalUser,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
